const INLINE_TAG_PATTERN = /<\/?(strong|b|em|i|u|mark|a|br|s|strike|del)\b[^>]*>/gi;
const BLOCK_TAG_PATTERN = /^<(ul|ol|blockquote|pre|hr)\b/i;
const IMAGE_PLACEHOLDER_PATTERN = /^!\[图片占位：(.+?)\]\(\)\s*(.+)$/;

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function renderImagePlaceholderBlock(content) {
  const match = String(content || "").match(IMAGE_PLACEHOLDER_PATTERN);
  if (!match) return "";

  const title = match[1].trim();
  const rest = match[2].trim();
  const colonIndex = rest.indexOf("：");
  const placement = colonIndex === -1 ? rest : rest.slice(0, colonIndex).trim();
  const description = colonIndex === -1 ? "" : rest.slice(colonIndex + 1).trim();
  const raw = escapeHtml(String(content));

  return `<figure class="zf-image-placeholder" data-placeholder="${raw}">
  <div class="zf-image-placeholder__frame">
    <span class="zf-image-placeholder__badge">图片占位</span>
    <strong class="zf-image-placeholder__title">${escapeHtml(title)}</strong>
    <span class="zf-image-placeholder__meta">${escapeHtml(placement)}${description ? `：${escapeHtml(description)}` : ""}</span>
  </div>
</figure>`;
}

export function renderRichBlockHtml(content) {
  const placeholder = renderImagePlaceholderBlock(content);
  if (placeholder) return placeholder;

  const value = String(content || "").trim();
  if (!value) return "";

  if (BLOCK_TAG_PATTERN.test(value)) {
    return sanitizeBlockHtml(value);
  }

  if (/[<>]/.test(value)) {
    return `<p>${normalizeEditorHtml(value)}</p>`;
  }

  return `<p>${escapeHtml(value)}</p>`;
}

export function stripRichText(value) {
  return String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\u00a0/g, " ")
    .trim();
}

export function sanitizeInlineHtml(raw) {
  let value = String(raw || "");
  value = value.replace(/<\/?(script|style|iframe|object|embed)[^>]*>/gi, "");
  value = value.replace(/<(\/?)([\w:-]+)([^>]*)>/gi, (match, closing, tagName, attrs) => {
    const tag = tagName.toLowerCase();
    if (!["strong", "b", "em", "i", "u", "mark", "a", "br", "s", "strike", "del"].includes(tag)) return "";
    if (closing) return `</${tag}>`;
    if (tag === "br") return "<br>";
    if (tag === "a") {
      const href = attrs.match(/\shref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const url = href?.[2] || href?.[3] || href?.[4] || "";
      if (!/^https?:\/\//i.test(url)) return "";
      return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">`;
    }
    return `<${tag}>`;
  });
  return value.trim();
}

export function normalizeEditorHtml(raw, { allowBlockTags = false } = {}) {
  let value = String(raw || "");
  value = value.replace(/<span\b([^>]*)>([\s\S]*?)<\/span>/gi, (match, attrs, content) => {
    const style = attrs.match(/\sstyle=(["'])(.*?)\1/i)?.[2] || "";
    const normalizedStyle = style.replace(/\s+/g, " ").toLowerCase();
    if (/font-weight:\s*(bold|[7-9]00)/.test(normalizedStyle)) return `<strong>${content}</strong>`;
    if (/font-style:\s*italic/.test(normalizedStyle)) return `<em>${content}</em>`;
    if (/text-decoration:[^;"]*underline/.test(normalizedStyle)) return `<u>${content}</u>`;
    if (/text-decoration:[^;"]*line-through/.test(normalizedStyle)) return `<s>${content}</s>`;
    if (/background-color:/.test(normalizedStyle)) return `<mark>${content}</mark>`;
    return content;
  });
  value = value.replace(/<font\b[^>]*>([\s\S]*?)<\/font>/gi, "$1");
  return allowBlockTags ? sanitizeListHtml(value) : sanitizeInlineHtml(value);
}

function normalizeBlockquoteHtml(raw) {
  const value = String(raw || "");
  const paragraphMatches = [...value.matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi)];
  if (paragraphMatches.length) {
    return paragraphMatches
      .map((match) => normalizeEditorHtml(match[1]))
      .filter(Boolean)
      .join("<br><br>");
  }
  return normalizeEditorHtml(value);
}

export function sanitizeListHtml(raw) {
  let value = String(raw || "");
  value = value.replace(/<\/?(?!ul|ol|li|strong|b|em|i|u|mark|a|br)\w+[^>]*>/gi, "");
  value = value.replace(/<(ul|ol|li)\b[^>]*>/gi, (match, tag) => `<${tag.toLowerCase()}>`);
  value = value.replace(/<a\b([^>]*)>/gi, (match, attrs) => {
    const href = attrs.match(/\shref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i);
    const url = href?.[2] || href?.[3] || href?.[4] || "";
    if (!/^https?:\/\//i.test(url)) return "";
    return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">`;
  });
  return value.trim();
}

function sanitizeBlockHtml(raw) {
  if (/^<hr\b/i.test(raw)) return "<hr>";
  if (/^<pre\b/i.test(raw)) {
    const inner = raw.replace(/^<pre[^>]*>\s*<code[^>]*>/i, "").replace(/<\/code>\s*<\/pre>$/i, "");
    return `<pre><code>${inner.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, "").replace(/<[^>]+>/g, "")}</code></pre>`;
  }
  if (/^<blockquote/i.test(raw)) {
    const inner = raw.replace(/^<blockquote[^>]*>/i, "").replace(/<\/blockquote>$/i, "");
    return `<blockquote>${normalizeBlockquoteHtml(inner)}</blockquote>`;
  }
  return sanitizeListHtml(raw);
}

export function paragraphHasRichText(value) {
  return INLINE_TAG_PATTERN.test(String(value || "")) || BLOCK_TAG_PATTERN.test(String(value || ""));
}
